import React, { useContext } from 'react';

import { type Dispatch, compose } from 'redux';

import { reloadRules as reloadClickupRules } from 'common/actions/clickupRules';
import { reloadCompany } from 'common/actions/company';
import { reloadRules as reloadJiraRules } from 'common/actions/jiraRules';
import { invalidatePostQueries } from 'common/actions/postQueries';
import { invalidateAllPostActivities } from 'common/actions/postsActivity';
import { invalidateSuggestions } from 'common/actions/postSuggestions';
import { reloadRoadmap } from 'common/actions/roadmap';
import AJAX from 'common/AJAX';
import { CompanyContext } from 'common/containers/CompanyContainer';
import connect from 'common/core/connect';
import Helmet from 'common/helmets/Helmet';
import withAccessControl from 'common/routing/withAccessControl';
import { hexToStatusColor } from 'common/statuses/StatusColors';
import AdminStatusSettings, {
  type SaveStatusesResponse,
  type StatusItem,
} from 'common/subdomain/admin/AdminStatusSettings';
import mapify from 'common/util/mapify';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';
import 'css/components/subdomain/admin/_AdminPostStatusSettings.scss';

import AdminSettingsHeader from './AdminSettings/AdminSettingsHeader';

type OwnProps = Record<string, never>;

type ConnectProps = {
  reloadAll: () => Promise<void>;
};

type Props = OwnProps & ConnectProps;

const statusesToPayload = (statuses: StatusItem[]) => {
  const statusMap = mapify(statuses, 'tempID');
  return statuses
    .map((status) => ({
      ...status,
      ...(status.targetStatus && { targetStatus: statusMap[status.targetStatus].name }),
      color: hexToStatusColor(status.color),
    }))
    .filter((status) => !(status.new && status.delete));
};

const AdminPostStatusSettings = ({ reloadAll }: Props) => {
  const company = useContext(CompanyContext);

  const saveStatuses = async (statuses: StatusItem[]): Promise<SaveStatusesResponse> => {
    const response = await AJAX.post('/api/postStatuses/update', {
      statuses: statusesToPayload(statuses),
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    if (error) {
      return { error: error.message, success: false };
    }

    // reload related data
    await reloadAll();

    return { success: true };
  };

  return (
    <div className="adminPostStatusSettings">
      <Helmet title="Public Statuses | Canny" />
      <AdminSettingsHeader
        title="Public Statuses"
        subheading="Manage your status&nbsp;labels."
        learnMoreLink="https://help.canny.io/en/articles/673583-post-statuses"
      />
      <div className="settingsContent">
        <AdminStatusSettings
          entity="post"
          showRoadmap={true}
          statuses={company.statuses}
          saveStatuses={saveStatuses}
        />
      </div>
    </div>
  );
};

export default compose(
  connect(null, (dispatch: Dispatch<any>) => ({
    reloadAll: () => {
      return Promise.all([
        dispatch(reloadClickupRules()),
        dispatch(reloadCompany()),
        dispatch(reloadJiraRules()),
        dispatch(reloadRoadmap()),
        dispatch(invalidateAllPostActivities()),
        dispatch(invalidatePostQueries()),
        dispatch(invalidateSuggestions()),
      ]);
    },
  })),
  withAccessControl(
    testEveryPermission(RoutePermissions.adminSettings.publicStatuses.statuses),
    '/admin/settings',
    { forwardRef: true },
  ),
)(AdminPostStatusSettings) as unknown as React.FC<OwnProps>;
